import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { useSelector } from 'react-redux';
import { MassageControlValueContext } from '@/pages/service/massage-control';
import { selectCurrentServingOrder } from '@/store/service';
import When from '../When';
import { Canvas2DViewer } from './viewer';

import cx from './index.module.scss';

interface Body2DViewerProp {
  shouldRenderOpacityNodule: boolean;
  points: Palpation.PointTypeWithDirection[];
  imgUrl: string;
  hasCustomRouteLine: boolean;
  onImageLoaded?: VoidFunc;
}

export interface RefAttrs {
  screenshot: () => string;
  renderOpacityPoints: () => void;
  renderPoints: () => void;
  updateDottedLinePoints: (coords: number[]) => void;
  /** 绘制虚线箭头 */
  drawDottedCircleLine: (
    coord: number[],
    config: {
      lineId: string;
      stroke: string;
      dash?: number[];
      opacity?: number;
    }
  ) => void;
  /** 改变虚线箭头颜色 */
  changeDottedLineCircleColor: (lineId: string, color: string) => void;
  /** 删除所有的虚线箭头 */
  deleteAllDottedLineCircle: VoidFunc;
  /** 根据ID删除虚线箭头 */
  deleteDottedLineCircleByIds: (lineId: string[]) => void;
  animateDottedLine: (lineId: string) => void;
  drawImage: (
    points: Array<{
      coord: number[];
      imageConfig: {
        url: string;
        size: number[];
      };
    }>
  ) => void;
}

const Body2DViewer = forwardRef<RefAttrs, Body2DViewerProp>(
  ({ imgUrl, points, onImageLoaded, hasCustomRouteLine = false, shouldRenderOpacityNodule = false }, ref) => {
    const { massageState } = useContext(MassageControlValueContext);
    const viewerRef = useRef<Canvas2DViewer>(undefined);
    const currentServingOrder = useSelector(selectCurrentServingOrder);

    const containerRef = useRef<any>();
    const [forceUpdatePoints, setForceUpdatePoints] = useState({});

    useImperativeHandle(
      ref,
      () => {
        return {
          screenshot: () => viewerRef.current?.screenshot(),
          renderOpacityPoints: () => {
            if (viewerRef.current) {
              viewerRef.current.updatePoints({
                points,
                isSmall: true,
                isNeedOpacity: true,
              });
            } else {
              console.error('未初始化');
            }
          },
          renderPoints: () => {
            if (viewerRef.current) {
              viewerRef.current.updatePoints({
                points: points,
                isSmall: true,
              });
            } else {
              console.error('未初始化');
            }
          },
          updateDottedLinePoints: (coords: number[]) => {
            viewerRef.current.updateDottedLinePoints(coords);
          },
          drawDottedCircleLine: (...args: any) => {
            viewerRef.current.drawDottedCircleLine?.apply(viewerRef.current, args);
          },
          changeDottedLineCircleColor: (...args: any) => {
            viewerRef.current.changeDottedLineCircleColor?.apply(viewerRef.current, args);
          },
          deleteDottedLineCircleByIds: (lineId: string[]) => {
            viewerRef.current?.deleteDottedLineCircleByIds(lineId);
          },
          deleteAllDottedLineCircle: () => {
            viewerRef.current?.deleteAllDottedLineCircle();
          },
          animateDottedLine: (lineId: string) => {
            viewerRef.current?.animateDottedLine(lineId);
          },
          drawImage: (
            points: Array<{
              coord: number[];
              imageConfig: {
                url: string;
                size: number[];
              };
            }>
          ) => {
            if (!viewerRef.current) return;

            viewerRef.current.clearDrawedImage();
            if (!points?.length) return;

            points?.forEach(item => {
              item.coord?.length && viewerRef.current.drawImage(item.coord, item.imageConfig);
            });
          },
        };
      },
      [points]
    );

    const initViewer = useCallback(async () => {
      const viewer = new Canvas2DViewer({
        containerId: 'canvas-2d-viewer',
        containerWidth: containerRef.current.offsetWidth,
        containerHeight: containerRef.current.offsetHeight,
        imgUrl,
        pointArr: [],
        onImageLoaded: () => {
          // 先出动画，在进行人体描边
          setForceUpdatePoints({});
          onImageLoaded?.();
        },
      });
      await viewer.init();

      viewerRef.current = viewer;

      viewerRef.current.updatePoints({
        points,
        isSmall: true,
      });
    }, [imgUrl, onImageLoaded, points]);

    useEffect(() => {
      if (currentServingOrder?.orderId) {
        initViewer();
        return () => {
          viewerRef.current?.destroy();
          viewerRef.current = null;
        };
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentServingOrder?.orderId]);

    useEffect(() => {
      if (viewerRef.current) {
        viewerRef.current.updatePoints({
          points,
          isSmall: true,
          isNeedOpacity: shouldRenderOpacityNodule,
        });
      }
    }, [points, forceUpdatePoints, shouldRenderOpacityNodule]);

    return (
      <div className={cx('canvas-viewer-wrap')}>
        <div className={cx('pic-wrapper')}>
          <div className={cx('header')}>
            <When condition={massageState !== 'Preparing_AcupointsCalibration'}>
              <Icon className={cx('icon')} name="tips" />
            </When>
            <span>
              {['Preparing_KnotsMarking', 'Preparing_Setting'].includes(massageState)
                ? '痛点标记数量（最多标记16处）'
                : massageState === 'Preparing_AcupointsCalibration'
                ? '触诊影像采集框'
                : '自定义画线数量（最多支持4条）'}
            </span>
          </div>

          <div className={cx('main-content')}>{!!imgUrl && <div className={cx('main-content-animation')}></div>}</div>

          <div className={cx('footer')}>
            {['Preparing_KnotsMarking', 'Preparing_Setting'].includes(massageState) ? (
              <>
                请在
                <span className={cx('highlight')}>虚线框内</span>
                标记痛点
              </>
            ) : massageState === 'Preparing_AcupointsCalibration' ? (
              <>
                请确保
                <span className={cx('highlight')}>头部、肩部、胯部</span>
                均在
                <br />
                图像采集框中
              </>
            ) : (
              <>
                请在
                <span className={cx('highlight')}>虚线框内</span>
                画线
                {!!hasCustomRouteLine && (
                  <>
                    ，点击右侧「线路」按钮
                    <br />
                    可查看对应画线
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <div ref={containerRef} id="canvas-2d-viewer" className={cx('canvas-viewer')}></div>
      </div>
    );
  }
);

export default Body2DViewer;
export type { RefAttrs as Body2DViewerRefAttrs };
